/**
 * ConfigureConnector - Step 2 of Add Connector Wizard
 *
 * Renders a connector identity chip, a flat "General" section, and the
 * schema-driven DynamicConnectorForm. No per-connector form code is needed --
 * the backend schema drives the connector-specific fields. (#260 redesign:
 * flat sections, no cards, no Advanced card, no trailing alerts.)
 */
import React, { useCallback, useMemo } from 'react';
import { Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { ConnectorType, QueryFile } from '@/types/connectors';
import { QueryFileUpload } from '@/components/connectors/QueryFileUpload';
import { PlatformLogo } from '@/components/connectors/library/PlatformLogo';
import { DynamicConnectorForm } from './dynamic/DynamicConnectorForm';
import { ConfigureHelpCallout } from './page/ConfigureHelpCallout';
import { useConnectorTypes, useConnectorTypeSchema } from '@/hooks/useConnectorTypes';

export interface ConnectorConfigFormData {
  name: string;
  description: string;
  enabled: boolean;
  // Dynamic fields from schema (keyed by field name)
  [key: string]: unknown;
  // Scheduling is collected on the dedicated Schedule step (#273) for
  // connector types that support it.
  schedule_cron?: string;
  schedule_enabled?: boolean;
  schedule_timezone?: string;
  // Query file
  queryFile?: QueryFile | null;
}

interface ConfigureConnectorProps {
  connectorType: ConnectorType;
  formData: ConnectorConfigFormData;
  onChange: (data: ConnectorConfigFormData) => void;
  errors: Partial<Record<keyof ConnectorConfigFormData, string>>;
  onValidate: () => boolean;
}

/**
 * Form field wrapper with label and error display
 */
const FormField: React.FC<{
  label: string;
  required?: boolean;
  description?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, required, description, error, children }) => {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {children}
      {description && !error && (
        <p className="text-xs text-muted-foreground flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <span>{description}</span>
        </p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
};

/**
 * Main ConfigureConnector Component
 */
export const ConfigureConnector: React.FC<ConfigureConnectorProps> = ({
  connectorType,
  formData,
  onChange,
  errors,
}) => {
  const { data: connectorTypesData } = useConnectorTypes();
  // Schema is already fetched (and cached) by the wizard controller; this reuses
  // the cached query to derive the per-connector help callout (#233).
  const { data: schema } = useConnectorTypeSchema(connectorType, !!connectorType);
  const connectorTypeInfo = useMemo(
    () => connectorTypesData?.connector_types?.find(t => t.connector_type === connectorType),
    [connectorTypesData, connectorType]
  );

  const handleFieldChange = <K extends keyof ConnectorConfigFormData>(
    field: K,
    value: ConnectorConfigFormData[K]
  ) => {
    onChange({ ...formData, [field]: value });
  };

  // Adapter for DynamicConnectorForm's onChange signature
  const handleDynamicFormChange = useCallback((values: Record<string, unknown>) => {
    onChange(values as ConnectorConfigFormData);
  }, [onChange]);

  return (
    <div className="space-y-8">
      {/* Connector identity chip */}
      {connectorTypeInfo && (
        <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
          <PlatformLogo connectorType={connectorType} size={24} />
          <div>
            <div className="text-sm font-medium">{connectorTypeInfo.display_name}</div>
            <div className="text-xs capitalize text-muted-foreground">
              {connectorTypeInfo.category}
            </div>
          </div>
        </div>
      )}

      {/* What you'll need -- inline per-connector help (#233). Docs links come
          from the connector-types list (the /schema endpoint omits them). */}
      <ConfigureHelpCallout
        connectorType={connectorType}
        schema={schema}
        links={{
          documentation_url: connectorTypeInfo?.documentation_url,
          setup_guide_url: connectorTypeInfo?.setup_guide_url,
        }}
      />

      {/* General */}
      <section className="space-y-4">
        <h3 className="text-base font-semibold tracking-tight">General</h3>

        <FormField
          label="Connector Name"
          required
          description="A descriptive name for this connector instance"
          error={errors.name}
        >
          <Input
            value={formData.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            placeholder="e.g., Marketing Google Ads"
            className={cn(errors.name && 'border-destructive')}
          />
        </FormField>

        <FormField
          label="Description"
          description="Optional description of this connector's purpose"
          error={errors.description}
        >
          <Textarea
            value={formData.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            placeholder="e.g., Production Google Ads account for marketing team"
            rows={3}
          />
        </FormField>

        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <Label htmlFor="enabled" className="text-sm font-medium">
              Enable immediately
            </Label>
            <p className="text-xs text-muted-foreground mt-1">
              Start using this connector right after creation
            </p>
          </div>
          <Switch
            id="enabled"
            checked={formData.enabled}
            onCheckedChange={(checked) => handleFieldChange('enabled', checked)}
          />
        </div>
      </section>

      {/* Connector-specific schema-driven fields (flat sections + OAuth gate) */}
      <div className="border-t pt-8">
        <DynamicConnectorForm
          connectorType={connectorType}
          values={formData as unknown as Record<string, unknown>}
          onChange={handleDynamicFormChange}
          externalErrors={errors as Record<string, string>}
        />
      </div>

      {/* Query Configuration - only for connectors without resource selection */}
      {!connectorTypeInfo?.has_resource_selection && (
        <div className="border-t pt-8">
          <QueryFileUpload
            connectorType={connectorType}
            value={formData.queryFile}
            onChange={(file) => handleFieldChange('queryFile', file)}
            error={errors.queryFile}
            disabled={false}
          />
        </div>
      )}
    </div>
  );
};
